import {
  Collapse,
  Link as LinkMaterialUi,
  List,
  ListItem,
  ListItemText,
  makeStyles,
} from '@material-ui/core';
import ExitToApp from '@material-ui/icons/ExitToApp';
import { globalHistory, Link, navigate } from '@reach/router';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../../contexts/AuthContext/AuthContext';
import { clearSearchCache } from '../../../core/api/cache';
import { CONSTANTS } from '../../../core/config/constants';
import { environment } from '../../../core/config/environment';
import { BorderWidthWithColor } from '../../../core/config/mainTheme';
import { hasAdminAccess, hasBenchwiseAccess } from '../../../core/utils/access.utils';
import { ROUTES } from '../../../routes';
import ActionBox from '../../TableGrid/TableGridToolbar/ActionBox/ActionBox';
import { ActionLink } from '../../TableGrid/TableGridToolbar/ActionBox/ActionBox.type';

interface OwnProps {}

interface SubMenu {
  title: string;
  route: string;
  isGuardItem?: boolean;
  hidden?: boolean;
}

const Navigation: React.FC<OwnProps> = () => {
  const classes = useStyles();
  const [subMenu, setSubMenu] = useState<SubMenu[]>([]);
  const [path, setPath] = useState(window.location.pathname);
  const { t } = useTranslation();
  const { user, logout, clearSessions } = useAuth();
  const benchwiseEnabled = hasBenchwiseAccess(user);
  const adminAccess = hasAdminAccess(user);

  const mainMenu = useMemo(() => {
    const externalLinks =
      environment.ENV === 'prod' ? ROUTES.externalLinks.prod : ROUTES.externalLinks.demo;

    return [
      {
        title: t('app:navTab.benchwisePrefix'),
        route: ROUTES.benchmarking.benchwise,
        isGuardItem: true,
        hidden: !benchwiseEnabled,
        aiText: true,
        submenu: [
          {
            title: t('app:navTab.benchwiseNewStudy'),
            route: ROUTES.benchmarking.benchwise,
            hidden: !benchwiseEnabled,
            isGuardItem: true,
          },
          {
            title: t('app:navTab.benchwiseExistingStudies'),
            route: ROUTES.benchmarking.benchwiseStudyList,
            hidden: !benchwiseEnabled,
            isGuardItem: true,
          },
        ],
      },
      {
        title: t('app:navTab.financialDb'),
        route: ROUTES.financialData.page,
        submenu: [
          {
            title: t('app:navTab.financialData'),
            route: ROUTES.financialData.page,
            isGuardItem: true,
          },
          {
            title: t('app:navTab.mySearches'),
            route: ROUTES.mySearches.page,
            isGuardItem: true,
          },
          {
            title: t('app:navTab.allSearches'),
            route: ROUTES.allSearches.page,
            hidden: !adminAccess,
            isGuardItem: true,
          },
          {
            title: t('app:navTab.help'),
            route: ROUTES.help.page,
            isGuardItem: false,
          },
        ] as SubMenu[],
      },
      {
        title: t('app:navTab.royaltyAi'),
        route: externalLinks.royaltyAi,
        external: true,
        hidden: !user?.royaltyAiEnabled,
      },
      {
        title: t('app:navTab.royaltySearch'),
        route: externalLinks.royaltySearch,
        external: true,
      },
      {
        title: t('app:navTab.serviceSearch'),
        route: externalLinks.serviceSearch,
        external: true,
      },
      {
        title: t('app:navTab.loanSearch'),
        route: externalLinks.loanSearch,
        external: true,
      },
      {
        title: t('app:navTab.pillars'),
        route: externalLinks.pillars,
        external: true,
        hidden: !user?.pillarsAccess,
      },
      {
        title: t('app:navTab.dempeAnalytics'),
        route: externalLinks.dempeAnalitics,
        external: true,
      },
      {
        title: t('app:navTab.creditRatingTool'),
        route: externalLinks.creditRatingTool,
        external: true,
      },
      {
        title: t('app:navTab.benchmarkingTool'),
        route: externalLinks.benchmarkingTool,
        external: true,
      },
      {
        title: t('app:navTab.benchmarking'),
        route: ROUTES.benchmarking.page,
        submenu: [
          {
            title: t('app:navTab.benchmarkingData'),
            route: ROUTES.benchmarking.page,
            isGuardItem: true,
          },
          {
            title: t('app:navTab.benchwiseNewStudy'),
            route: ROUTES.benchmarking.benchwise,
            hidden: !benchwiseEnabled,
            isGuardItem: true,
          },
        ] as SubMenu[],
      },
    ];
  }, [t, user, benchwiseEnabled, adminAccess]);

  const getUserActionItems = (): ActionLink[] => [
    {
      title: 'companies:clearVwbSessions',
      onClick: clearSessions,
      isNotActive: !user?.canAccessPublicDb,
      isHidden: false,
    },
    {
      title: 'companies:clearSearchCache',
      onClick: clearSearchCache,
      isNotActive: !user?.isAdminUser,
      isHidden: !user?.isAdminUser,
    },
  ];

  useEffect(() => {
    return globalHistory.listen(() => setPath(window.location.pathname));
  }, []);

  useEffect(() => {
    if (path === '/') {
      setSubMenu(mainMenu[0].submenu ?? []);
    } else {
      mainMenu.forEach((m) => {
        if (path.indexOf(m.route) !== -1) {
          setSubMenu(m.submenu?.filter((element) => !element.hidden) ?? []);
        }
      });
    }
  }, [setSubMenu, mainMenu, path]);

  const NavLink = (props: any) => (
    <Link
      {...props}
      getProps={({ isCurrent }) => {
        return isCurrent ? { className: 'activeLink' } : {};
      }}
    />
  );

  const handleClick = (submenu: SubMenu[] | undefined, route: string) => {
    if (submenu) {
      setSubMenu(submenu);
      navigate(route);
    }
  };

  return (
    <div className={classes.root}>
      <List component="ul" aria-labelledby="nested-list-subheader" className={classes.mainMenu}>
        {mainMenu
          .filter((element) => !element.hidden)
          .map((link, index) => (
            <ListItem
              key={index}
              onClick={() => handleClick(link.submenu, link.route)}
              selected={path.indexOf(link.route) !== -1}
              className={link.submenu ? `hasSubmenu ${classes.listItem}` : `${classes.listItem}`}
            >
              {link.external ? (
                <LinkMaterialUi href={link.route} component="a">
                  {link.title}
                </LinkMaterialUi>
              ) : link.submenu ? (
                <>
                  <ListItemText primary={link.title} />
                  {link.aiText && <span className={classes.aiGradient}>[AI]</span>}
                </>
              ) : (
                <NavLink to={link.route}>{link.title}</NavLink>
              )}
            </ListItem>
          ))}
        {user && (
          <>
            <ActionBox
              buttonTitle={user.fullName}
              actionItems={getUserActionItems()}
              externalClasses={classes}
              boxVariant="outlined"
            />
            <ListItem onClick={logout} className={classes.listItem}>
              <ExitToApp className={classes.userButton} />
            </ListItem>
          </>
        )}
      </List>
      {subMenu.filter((element) => !element.hidden) ? (
        <Collapse in={true} timeout="auto" unmountOnExit className={classes.submenuBlock}>
          <List component="div">
            {subMenu
              .filter((element) => !element.hidden)
              .map((subItem, index) => (
                <ListItem
                  key={index}
                  className={`${classes.listItem} ${
                    subItem.isGuardItem && !user?.canAccess ? 'hidden' : ''
                  }`}
                >
                  <NavLink to={subItem.route}>{subItem.title}</NavLink>
                </ListItem>
              ))}
          </List>
        </Collapse>
      ) : null}
    </div>
  );
};

const useStyles = makeStyles((theme) => ({
  dropdownRoot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '160px',
    marginLeft: '65px',
  },
  dropdownLabel: {
    marginRight: theme.spacing(2),
  },
  dropdown: {
    width: '50%',
    '& .MuiInputBase-input': {
      minHeight: '18px',
      height: '18px',
    },
    '& .MuiInputBase-root': {
      minHeight: '32px',
    },
    marginRight: theme.spacing(5),
  },
  root: {
    width: '100%',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.secondary.dark,
    padding: 0,
    fontSize: theme.typography.fontSize,
  },
  mainMenu: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 0,
    paddingBottom: 0,
    '& > li': {
      display: 'flex',
    },
    minHeight: CONSTANTS.TOOLBAR_HEIGHT,
  },
  aiGradient: {
    background: 'linear-gradient(90deg, #00b4d8 0%, #43ea7f 40%, #3a86ff 70%, #38b000 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    fontWeight: 500,
    paddingLeft: 4,
    fontSize: '1rem',
    letterSpacing: '0.05em',
    display: 'inline-block',
    verticalAlign: 'middle',
  },
  listItem: {
    width: 'auto',
    color: theme.palette.secondary.dark,
    cursor: 'pointer',
    padding: '0 0 6px',
    marginRight: '16px',
    marginLeft: '16px',
    borderTop: '5px solid transparent',
    borderBottom: '5px solid transparent',
    '&.hasSubmenu.Mui-selected': {
      borderBottom: `5px solid ${theme.palette.primary.main}`,
      background: 'transparent',
      color: theme.palette.primary.dark,
      '&:hover': {
        borderBottom: `5px solid ${theme.palette.primary.main}`,
        background: 'none',
      },
    },
    '& .activeLink': {
      color: theme.palette.primary.dark,
      fontWeight: 'bold',
      '& svg': {
        color: theme.palette.primary.dark,
      },
    },
    '& .MuiTypography-body1': {
      fontSize: theme.typography.fontSize,
    },
    '& label': {
      color: theme.palette.secondary.dark,
      fontFamily: theme.typography.fontFamily,
      fontSize: theme.typography.fontSize,
    },
    '& a': {
      color: theme.palette.secondary.dark,
      fontFamily: theme.typography.fontFamily,
      textDecoration: 'none',
      fontSize: theme.typography.fontSize,
      cursor: 'pointer',
      padding: 0,
      border: 'none',
      background: 'none',
      '&:hover': {
        textDecoration: 'none',
      },
      '&:focus': {
        textDecoration: 'none',
      },
    },
    '&.hidden': {
      display: 'none',
    },
  },
  userButton: {
    color: theme.palette.secondary.dark,
  },
  userName: {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize,
    padding: '0 0 6px',
    borderTop: '5px solid transparent',
    borderBottom: '5px solid transparent',
    marginLeft: 'auto',
  },
  submenuBlock: {
    display: 'block',
    background: theme.palette.secondary.main,
    borderTop: `2px solid ${theme.palette.divider}`,
    borderLeft: `1px solid ${theme.palette.divider}`,
    marginTop: '-2px',
    clear: 'both',
    height: CONSTANTS.TOOLBAR_HEIGHT,
    '& li': {
      display: 'inline-block',
    },
    '& .MuiList-root': {
      paddingBottom: 0,
      paddingTop: 0,
    },
  },
  menuBox: {
    zIndex: 2,
    borderTop: BorderWidthWithColor(1, theme.palette.primary.contrastText),
  },
  button: {
    textTransform: 'none',
    background: 'none',
    color: theme.palette.secondary.dark,
    borderRadius: 0,
    border: 'none',
    '& svg': {
      marginLeft: '10px',
    },
    '&:hover': {
      background: 'none',
      border: 'none',
    },
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize,
    padding: '0 0 6px',
    borderTop: '5px solid transparent',
    borderBottom: '5px solid transparent',
    marginLeft: 'auto',
  },
  menuItems: {
    background: 'none',
  },
}));

export default Navigation;
